import React, { useRef, useState } from 'react';
import { useSharePoint, DriveItem } from './api/useSharePoint';

// ─────────────────────────────────────────────────────────────────────────────
// Shared UI primitives
// ─────────────────────────────────────────────────────────────────────────────

function Spinner({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) {
  const s = size === 'sm' ? 'w-4 h-4' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6';
  return (
    <svg className={`${s} animate-spin text-violet-500`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Modals
// ─────────────────────────────────────────────────────────────────────────────

function ModalShell({ children }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl w-full max-w-sm">
        {children}
      </div>
    </div>
  );
}

function NewFolderModal({
  onClose,
  onCreate,
}: {
  onClose: () => void;
  onCreate: (name: string) => Promise<boolean>;
}) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setBusy(true);
    setErr(null);
    const ok = await onCreate(trimmed);
    setBusy(false);
    if (ok) onClose();
    else setErr('Failed to create folder. Try again.');
  }

  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
        <h2 className="text-base font-semibold text-white">New folder</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        {err && (
          <div className="flex items-start gap-2 p-3 bg-red-950/40 border border-red-800/50 rounded-xl">
            <p className="text-sm text-red-300">{err}</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">Folder name</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="New folder"
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder-gray-600 focus:outline-none focus:border-violet-500 transition-colors"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-xl transition-colors flex items-center gap-2"
          >
            {busy && <Spinner size="sm" />}
            Create
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function RenameModal({
  item,
  onClose,
  onRename,
}: {
  item: DriveItem;
  onClose: () => void;
  onRename: (item: DriveItem, newName: string) => Promise<boolean>;
}) {
  const [name, setName] = useState(item.name);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || trimmed === item.name) { onClose(); return; }
    setBusy(true);
    setErr(null);
    const ok = await onRename(item, trimmed);
    setBusy(false);
    if (ok) onClose();
    else setErr('Failed to rename. Try again.');
  }

  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
        <h2 className="text-base font-semibold text-white">Rename</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
        {err && (
          <div className="flex items-start gap-2 p-3 bg-red-950/40 border border-red-800/50 rounded-xl">
            <p className="text-sm text-red-300">{err}</p>
          </div>
        )}
        <div>
          <label className="block text-sm font-medium text-gray-300 mb-1.5">New name</label>
          <input
            autoFocus
            value={name}
            onChange={e => setName(e.target.value)}
            className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white focus:outline-none focus:border-violet-500 transition-colors"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !name.trim()}
            className="px-4 py-2 text-sm font-medium bg-violet-600 hover:bg-violet-500 disabled:opacity-50 text-white rounded-xl transition-colors flex items-center gap-2"
          >
            {busy && <Spinner size="sm" />}
            Rename
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

function DeleteModal({
  item,
  onClose,
  onDelete,
}: {
  item: DriveItem;
  onClose: () => void;
  onDelete: (item: DriveItem) => Promise<boolean>;
}) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handleDelete() {
    setBusy(true);
    setErr(null);
    const ok = await onDelete(item);
    setBusy(false);
    if (ok) onClose();
    else setErr('Failed to delete. Try again.');
  }

  return (
    <ModalShell onClose={onClose}>
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
        <h2 className="text-base font-semibold text-white">Delete {item.folder ? 'folder' : 'file'}</h2>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-300 transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-6 py-5 space-y-4">
        {err && (
          <div className="flex items-start gap-2 p-3 bg-red-950/40 border border-red-800/50 rounded-xl">
            <p className="text-sm text-red-300">{err}</p>
          </div>
        )}
        <p className="text-sm text-gray-300">
          Are you sure you want to delete{' '}
          <span className="font-medium text-white">"{item.name}"</span>?
          {item.folder && ' All contents will be deleted.'}
          {' '}This can be recovered from the SharePoint recycle bin.
        </p>
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-400 hover:text-gray-200 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={busy}
            className="px-4 py-2 text-sm font-medium bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white rounded-xl transition-colors flex items-center gap-2"
          >
            {busy && <Spinner size="sm" />}
            Delete
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// File-type icons
// ─────────────────────────────────────────────────────────────────────────────

function FolderIcon() {
  return (
    <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 24 24">
      <path d="M10 4H2a2 2 0 00-2 2v12a2 2 0 002 2h20a2 2 0 002-2V8a2 2 0 00-2-2h-10l-2-2z" />
    </svg>
  );
}

function FileIcon({ mimeType }: { mimeType?: string }) {
  if (mimeType?.includes('pdf'))
    return <svg className="w-5 h-5 text-red-400" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13z" /></svg>;
  if (mimeType?.includes('image'))
    return <svg className="w-5 h-5 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 12.75V21h18v-8.25" /></svg>;
  if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel') || mimeType?.includes('csv'))
    return <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13zM8 13h8v1.5H8zm0 3h8v1.5H8zm0-6h4v1.5H8z" /></svg>;
  if (mimeType?.includes('presentation') || mimeType?.includes('powerpoint'))
    return <svg className="w-5 h-5 text-orange-400" fill="currentColor" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13z" /></svg>;
  return (
    <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6zm-1 7V3.5L18.5 9H13zM8 13h8v1H8zm0 3h8v1H8zm0-6h4v1H8z" />
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (!bytes || bytes === 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function formatDate(iso: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const SHAREPOINT_SITE_URL = 'https://socialsparkapp974.sharepoint.com/sites/SocialSparkWorkspace';

// ─────────────────────────────────────────────────────────────────────────────
// Icon buttons used in row actions
// ─────────────────────────────────────────────────────────────────────────────

function IconBtn({
  title,
  onClick,
  danger,
  children,
}: {
  title: string;
  onClick: () => void;
  danger?: boolean;
  children: React.ReactNode;
}) {
  return (
    <button
      title={title}
      onClick={onClick}
      className={`p-1.5 rounded-lg transition-colors ${
        danger
          ? 'text-gray-500 hover:text-red-400 hover:bg-red-950/30'
          : 'text-gray-500 hover:text-gray-200 hover:bg-gray-700'
      }`}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// File row
// ─────────────────────────────────────────────────────────────────────────────

function FileRow({
  item,
  onNavigate,
  onRename,
  onDelete,
}: {
  item: DriveItem;
  onNavigate: (item: DriveItem) => void;
  onRename: (item: DriveItem) => void;
  onDelete: (item: DriveItem) => void;
}) {
  const isFolder = Boolean(item.folder);

  return (
    <div className="flex items-center gap-4 px-4 py-3 border-b border-gray-800/60 hover:bg-gray-800/30 transition-colors group">
      {/* Icon */}
      <div className="flex-shrink-0 w-6 flex justify-center">
        {isFolder ? <FolderIcon /> : <FileIcon mimeType={item.file?.mimeType} />}
      </div>

      {/* Name */}
      <div className="flex-1 min-w-0">
        {isFolder ? (
          <button
            onClick={() => onNavigate(item)}
            className="text-sm font-medium text-white hover:text-violet-300 transition-colors text-left truncate max-w-full block"
          >
            {item.name}
          </button>
        ) : (
          <span className="text-sm text-gray-200 truncate block">{item.name}</span>
        )}
        {isFolder && item.folder && (
          <span className="text-xs text-gray-500">
            {item.folder.childCount} item{item.folder.childCount !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Size */}
      <div className="w-24 text-right text-sm text-gray-500 flex-shrink-0">
        {isFolder ? '—' : formatBytes(item.size)}
      </div>

      {/* Modified */}
      <div className="w-32 text-right text-sm text-gray-500 flex-shrink-0">
        {formatDate(item.lastModifiedDateTime)}
      </div>

      {/* Actions — always reserve space, show on hover */}
      <div className="w-28 flex justify-end items-center gap-0.5 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Download (files only) */}
        {!isFolder && item['@microsoft.graph.downloadUrl'] && (
          <a
            href={item['@microsoft.graph.downloadUrl']}
            download={item.name}
            title="Download"
            className="p-1.5 rounded-lg text-gray-500 hover:text-gray-200 hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          </a>
        )}
        {/* Open folder */}
        {isFolder && (
          <IconBtn title="Open" onClick={() => onNavigate(item)}>
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </IconBtn>
        )}
        {/* Rename */}
        <IconBtn title="Rename" onClick={() => onRename(item)}>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z" />
          </svg>
        </IconBtn>
        {/* Delete */}
        <IconBtn title="Delete" onClick={() => onDelete(item)} danger>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
          </svg>
        </IconBtn>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

type Modal =
  | { type: 'newFolder' }
  | { type: 'rename'; item: DriveItem }
  | { type: 'delete'; item: DriveItem };

export default function DocumentsPage(): React.ReactElement {
  const {
    items, loading, error, needsConsent, grantAccess,
    breadcrumb, navigate, goTo,
    upload, createFolder, renameItem, deleteItem,
  } = useSharePoint();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [modal, setModal] = useState<Modal | null>(null);

  function showToast(type: 'success' | 'error', message: string) {
    setToast({ type, message });
    setTimeout(() => setToast(null), 4000);
  }

  const currentFolderWebUrl =
    breadcrumb.length > 1
      ? items.find(i => i.id === breadcrumb[breadcrumb.length - 1].id)?.webUrl
      : undefined;
  const sharePointUrl = currentFolderWebUrl ?? SHAREPOINT_SITE_URL;

  const isPermissionError = error?.startsWith('PERMISSION:');
  const displayError = isPermissionError ? error!.replace('PERMISSION: ', '') : error;

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const ok = await upload(file);
    setUploading(false);
    showToast(ok ? 'success' : 'error', ok ? `"${file.name}" uploaded.` : 'Upload failed. Check your permissions.');
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  return (
    <div className="min-h-screen bg-gray-950">

      {/* Modals */}
      {modal?.type === 'newFolder' && (
        <NewFolderModal
          onClose={() => setModal(null)}
          onCreate={async name => {
            const ok = await createFolder(name);
            if (ok) showToast('success', `Folder "${name}" created.`);
            return ok;
          }}
        />
      )}
      {modal?.type === 'rename' && (
        <RenameModal
          item={modal.item}
          onClose={() => setModal(null)}
          onRename={async (item, newName) => {
            const ok = await renameItem(item, newName);
            if (ok) showToast('success', `Renamed to "${newName}".`);
            return ok;
          }}
        />
      )}
      {modal?.type === 'delete' && (
        <DeleteModal
          item={modal.item}
          onClose={() => setModal(null)}
          onDelete={async item => {
            const ok = await deleteItem(item);
            if (ok) showToast('success', `"${item.name}" deleted.`);
            return ok;
          }}
        />
      )}

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="relative overflow-hidden py-14 px-6 border-b border-gray-800/50">
        <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-950 to-slate-950" />
        <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_20%_30%,rgba(124,58,237,0.4)_0%,transparent_55%),radial-gradient(circle_at_80%_70%,rgba(236,72,153,0.3)_0%,transparent_55%)]" />
        <div className="relative z-10 max-w-[1400px] mx-auto flex items-center gap-3">
          <div className="p-2.5 bg-violet-600/20 border border-violet-500/30 rounded-xl">
            <svg className="w-6 h-6 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-extrabold text-white tracking-tight">Documents</h1>
            <p className="text-gray-400 text-sm mt-0.5">Browse and manage SocialSpark files.</p>
          </div>
        </div>
      </div>

      {/* ── Content ───────────────────────────────────────────────────────── */}
      <div className="max-w-[1400px] mx-auto px-6 py-8">

        {/* Toolbar */}
        <div className="flex items-center justify-between gap-4 mb-4 flex-wrap">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1 text-sm flex-wrap">
            {breadcrumb.map((entry, idx) => {
              const isLast = idx === breadcrumb.length - 1;
              return (
                <React.Fragment key={`${entry.id ?? 'root'}-${idx}`}>
                  {idx > 0 && <span className="text-gray-600 mx-0.5">/</span>}
                  {isLast ? (
                    <span className="text-gray-200 font-medium">{entry.name}</span>
                  ) : (
                    <button
                      onClick={() => goTo(idx)}
                      className="text-gray-400 hover:text-violet-300 transition-colors"
                    >
                      {entry.name}
                    </button>
                  )}
                </React.Fragment>
              );
            })}
          </nav>

          {/* Actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <a
              href={sharePointUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-3 py-2 text-xs font-medium bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 rounded-xl transition-colors flex items-center gap-1.5"
            >
              Open in SharePoint
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </a>
            <button
              onClick={() => setModal({ type: 'newFolder' })}
              className="px-3 py-2 text-xs font-medium bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 rounded-xl transition-colors flex items-center gap-1.5"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 10.5v6m3-3H9m4.06-7.19l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
              </svg>
              New Folder
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-3 py-2 text-xs font-medium bg-violet-600 hover:bg-violet-500 disabled:opacity-60 text-white rounded-xl transition-colors flex items-center gap-1.5"
            >
              {uploading ? (
                <><Spinner size="sm" /> Uploading…</>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                  </svg>
                  Upload
                </>
              )}
            </button>
            <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileChange} />
          </div>
        </div>

        {/* Toast */}
        {toast && (
          <div className={`mb-4 flex items-center gap-3 px-4 py-3 rounded-xl border ${
            toast.type === 'success'
              ? 'bg-emerald-950/50 border-emerald-800/50'
              : 'bg-red-950/40 border-red-800/50'
          }`}>
            {toast.type === 'success' ? (
              <svg className="w-4 h-4 text-emerald-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" /><path d="M12 8v4m0 4h.01" />
              </svg>
            )}
            <p className={`text-sm ${toast.type === 'success' ? 'text-emerald-300' : 'text-red-300'}`}>
              {toast.message}
            </p>
          </div>
        )}

        {/* File table */}
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          {/* Header */}
          <div className="flex items-center gap-4 px-4 py-2.5 border-b border-gray-800 bg-gray-900/80">
            <div className="w-6 flex-shrink-0" />
            <div className="flex-1 text-xs font-medium text-gray-500 uppercase tracking-wider">Name</div>
            <div className="w-24 text-right text-xs font-medium text-gray-500 uppercase tracking-wider flex-shrink-0">Size</div>
            <div className="w-32 text-right text-xs font-medium text-gray-500 uppercase tracking-wider flex-shrink-0">Modified</div>
            <div className="w-28 flex-shrink-0" />
          </div>

          {/* Loading */}
          {loading && (
            <div className="flex items-center justify-center py-16 gap-3">
              <Spinner size="lg" />
              <span className="text-gray-400 text-sm">Loading files…</span>
            </div>
          )}

          {/* Consent required */}
          {!loading && needsConsent && (
            <div className="px-6 py-12 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-violet-950/40 border border-violet-700/40 rounded-full mb-4">
                <svg className="w-6 h-6 text-violet-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                </svg>
              </div>
              <h3 className="text-white font-semibold mb-1">Permission required</h3>
              <p className="text-sm text-gray-400 mb-5 max-w-sm mx-auto">
                This app needs your permission to access SharePoint files via Microsoft Graph.
              </p>
              <button
                onClick={grantAccess}
                className="px-5 py-2.5 bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium rounded-xl transition-colors"
              >
                Grant access
              </button>
              {error && <p className="mt-3 text-xs text-red-400">{error}</p>}
            </div>
          )}

          {/* Error */}
          {!loading && !needsConsent && error && (
            <div className="px-6 py-10 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-red-950/30 border border-red-800/40 rounded-full mb-4">
                <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
              </div>
              <p className="text-sm text-red-300 mb-2 max-w-md mx-auto">{displayError}</p>
              {isPermissionError && (
                <p className="text-xs text-gray-500 mt-1">
                  Go to the{' '}
                  <a href="https://portal.azure.com" target="_blank" rel="noopener noreferrer" className="text-violet-400 hover:underline">
                    Azure portal
                  </a>
                  {' '}→ App registrations → API permissions → Grant admin consent.
                </p>
              )}
            </div>
          )}

          {/* Empty */}
          {!loading && !needsConsent && !error && items.length === 0 && (
            <div className="px-6 py-16 text-center">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-800 border border-gray-700 rounded-full mb-4">
                <svg className="w-6 h-6 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
                </svg>
              </div>
              <p className="text-sm text-gray-400">This folder is empty.</p>
              <p className="text-xs text-gray-600 mt-1">Upload a file or create a folder to get started.</p>
            </div>
          )}

          {/* File list — folders first */}
          {!loading && !needsConsent && !error && items.length > 0 && (
            <div>
              {[...items.filter(i => i.folder), ...items.filter(i => !i.folder)].map(item => (
                <FileRow
                  key={item.id}
                  item={item}
                  onNavigate={navigate}
                  onRename={item => setModal({ type: 'rename', item })}
                  onDelete={item => setModal({ type: 'delete', item })}
                />
              ))}
            </div>
          )}
        </div>

        <p className="mt-3 text-xs text-gray-600 text-right">
          Powered by{' '}
          <a
            href="https://learn.microsoft.com/en-us/graph/overview"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-gray-400 transition-colors"
          >
            Microsoft Graph
          </a>
        </p>
      </div>
    </div>
  );
}
